/* eslint-disable jsx-control-statements/jsx-use-if-tag */
import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Skeleton, Checkbox, Tooltip, Icon } from 'antd';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Hosts } from '~helpers/environments';
import { get } from 'lodash';
import {
  NewSoldOut as SoldOutIcon,
  PlaceholderImage as DefaultImageIcon,
  PropertyListBlacksheep as PropertyListBlacksheepIcon,
  PropertyListStar as PropertyListStarIcon,
  PropertyListMixRank as PropertyListMixRankIcon,
} from 'common/components/svgs';
import generatePath from '~settings/routing';
import { propertyState, propertyUnpublishReason } from '~constants';
import PendingProgress from 'common/components/pending-progress';
import { onlyViewPropertyRole } from '~client/helpers/auth';
import SyncType from 'common/components/sync-type';

const imageHost = Hosts.imageUrl;

const useStyles = makeStyles(
  theme => ({
    cardBox: {
      width: '100%',
      ...theme.flex.row,
      marginBottom: '12px',
      background: '#FFFFFF',
      borderRadius: '2px',
      overflow: 'hidden',
    },
    cardBoxSelected: {
      outline: '2px solid',
      outlineColor: theme.colors.mainGreen6,
      boxShadow: '0px 0px 24px 2px rgba(56, 178, 166, 0.09)',
    },
    stickImgBox: {
      width: '182px',
      height: '112px',
      position: 'relative',
      overflow: 'hidden',
      flexShrink: 0,
    },
    stickImg: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
    textOverFlow: {
      whiteSpace: 'nowrap',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    contentBox: {
      ...theme.flex.fillRow,
      minWidth: 0,
      '&>div': {
        flex: 1,
        marginRight: '56px',
        paddingTop: '32px',
        paddingBottom: '30px',
        '& > div': {
          width: '100%',
          ...theme.singleLineOverflow,
        },
        '&:first-child': {
          flex: 2,
          minWidth: 120,
          marginLeft: '24px',
        },
        '&:nth-child(2)': {
          minWidth: 64,
        },
        '&:nth-child(3)': {
          minWidth: 64,
        },
        '&:nth-child(4)': {
          minWidth: 64,
        },
        '&:nth-child(5)': {
          minWidth: 64,
        },
        '&:last-child': {
          marginRight: '24px',
        },
      },
    },
    statusBox: {
      padding: '2px 8px',
      borderRadius: '2px',
      position: 'absolute',
      left: '8px',
      top: '8px',
      fontSize: '12px',
      lineHeight: '20px',
      border: '1px solid blue',
    },
    cardSelect: {
      position: 'absolute',
      right: '8px',
      bottom: '8px',
    },
    cardSelectShadow: {
      '&:hover': {
        opacity: 1,
      },
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'white',
      backgroundColor: 'rgba(0,0,0,0.65)',
      position: 'absolute',
      transition: 'opacity 0.5s',
      opacity: 0,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
    new: {
      background: theme.colors.blue1,
      color: theme.colors.blue6,
      borderColor: theme.colors.blue4,
    },
    editing: {
      background: theme.colors.blue1,
      color: theme.colors.blue6,
      borderColor: theme.colors.blue4,
    },
    published: {
      background: theme.colors.mainGreen1,
      color: theme.colors.mainGreen6,
      borderColor: theme.colors.mainGreen3,
    },
    unpublished: {
      background: theme.colors.gray2,
      color: theme.colors.captionColor,
      borderColor: theme.colors.gray5,
    },

    name: {
      ...theme.typography.h4,
      marginBottom: '4px',
      color: theme.colors.titleColor,
    },
    address: {
      ...theme.typography.body2,
      color: theme.colors.bodyColor,
    },
    bookingType: {
      ...theme.typography.body2,
      color: theme.colors.disableColor,
      marginBottom: '4px',
    },
    constactSvg: {
      verticalAlign: 'middle',
      marginRight: '4px',
    },
    locationSvg: {
      verticalAlign: 'middle',
      marginRight: '9px',
    },

    description: {
      ...theme.typography.body2,
      color: theme.colors.titleColor,
    },
    soldoutImg: {
      opacity: 0.4,
    },
    unpublishedTooltip: {
      '& >h5': {
        margin: 0,
        fontWeight: 600,
        fontSize: 14,
        lineHeight: '22px',
        color: 'white',
      },
      '& >p': {
        margin: 0,
        fontSize: 14,
        lineHeight: '22px',
        color: 'white',
      },
    },
    rankTypeIconLayer: {
      clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%)',
      position: 'absolute',
      width: 32,
      height: 32,
      top: 0,
      right: 0,
      color: 'white',
    },
    rankTypeStar: {
      background: theme.colors.mainGreen6,
    },
    rankTypeBlackSheep: {
      background: theme.colors.gray7,
    },
    rankTypeMixRank: {
      background: theme.colors.blue7,
    },
    rankTypeIcon: {
      position: 'absolute',
      top: '4px',
      right: '4px',
    },
  }),
  { name: 'new-property-card' },
);

const Status = ({ status, unpublishedReason }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { statusBox } = classes;
  const statusClass = status.toLowerCase();

  return (
    <Choose>
      <When condition={status === 'UNPUBLISHED' && unpublishedReason}>
        <Tooltip
          placement="topLeft"
          title={
            <div className={classes.unpublishedTooltip}>
              <h5>{t('cms.properties_bulk_operation_confirm.modal.field.unpublish')}</h5>
              <p>
                {t(
                  `cms.properties_bulk_operation_confirm.modal.field.unpublish.option.${propertyUnpublishReason[unpublishedReason]}`,
                )}
              </p>
            </div>
          }
        >
          <div
            className={classNames(statusBox, classes[statusClass])}
            data-test-id="property_status_tag"
          >
            {t(`cms:cms.property_card.status.tag.${statusClass}`)}
            <Icon style={{ marginLeft: '4px', fontSize: '10.5px' }} type="info-circle" />
          </div>
        </Tooltip>
      </When>
      <Otherwise>
        <div
          className={classNames(statusBox, classes[statusClass])}
          data-test-id="property_status_tag"
        >
          {t(`cms:cms.property_card.status.tag.${statusClass}`)}
        </div>
      </Otherwise>
    </Choose>
  );
};

const RankIcon = ({ t, rankStarCn, rankStarRow, rankBlacksheepCn, rankBlacksheepRow }) => {
  const classes = useStyles();
  const { rankTypeIconLayer, rankTypeIcon, rankTypeStar, rankTypeBlackSheep, rankTypeMixRank } =
    classes;
  return (
    <Tooltip
      placement="topLeft"
      title={
        <div className={classes.unpublishedTooltip}>
          <h5>{t('cms.property_card.rank.title')}</h5>
          <p>
            {t('cms.property_card.rank.cn')}:{' '}
            {rankStarCn
              ? t('cms.properties.edit.tab_label.others.ranking_type_star')
              : rankBlacksheepCn
              ? t('cms.properties.edit.tab_label.others.ranking_type_black_sheep')
              : t('cms.properties.edit.tab_label.others.ranking_type_normal')}
          </p>
          <p>
            {t('cms.property_card.rank.row')}:{' '}
            {rankStarRow
              ? t('cms.properties.edit.tab_label.others.ranking_type_star')
              : rankBlacksheepRow
              ? t('cms.properties.edit.tab_label.others.ranking_type_black_sheep')
              : t('cms.properties.edit.tab_label.others.ranking_type_normal')}
          </p>
        </div>
      }
    >
      <Choose>
        <When condition={!rankBlacksheepCn && !rankBlacksheepRow && (rankStarCn || rankStarRow)}>
          <div className={`${rankTypeIconLayer} ${rankTypeStar}`}>
            <PropertyListStarIcon className={rankTypeIcon} />
          </div>
        </When>
        <When condition={(rankBlacksheepCn || rankBlacksheepRow) && !rankStarCn && !rankStarRow}>
          <div className={`${rankTypeIconLayer} ${rankTypeBlackSheep}`}>
            <PropertyListBlacksheepIcon className={rankTypeIcon} />
          </div>
        </When>
        <Otherwise>
          <div className={`${rankTypeIconLayer} ${rankTypeMixRank}`}>
            <PropertyListMixRankIcon className={rankTypeIcon} />
          </div>
        </Otherwise>
      </Choose>
    </Tooltip>
  );
};

const NewPropertyCard = props => {
  const { property, loading, selected, onSelect, selectable } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const contractUrl = generatePath('landlord', { landlordSlug: get(property?.landlord, 'slug') });
  const commissionUrl = generatePath(
    'landlord',
    { landlordSlug: get(property?.landlord, 'slug') },
    { tab: 'COMMISSION' },
  );

  const {
    cardBox,
    cardBoxSelected,
    cardSelect,
    stickImgBox,
    contentBox,
    stickImg,
    name,
    address,
    bookingType,
    description,
    soldoutImg,
    cardSelectShadow,
  } = classes;

  return (
    <Link
      to={
        get(property, 'slug')
          ? generatePath(
              'property.homepage',
              { propertySlug: get(property, 'slug') },
              { propertyId: get(property, 'id') },
            )
          : ''
      }
      disabled={!property || !property?.slug}
    >
      <div
        className={classNames(cardBox, {
          [cardBoxSelected]: selected,
        })}
      >
        <Skeleton
          active
          avatar={{
            size: 122,
            shape: 'square',
          }}
          loading={loading}
        >
          <div
            className={classNames(stickImgBox, {
              [soldoutImg]: get(property, 'state') === propertyState.SOLD_OUT,
            })}
          >
            <If condition={!get(property, 'heroImage.source')}>
              <DefaultImageIcon className={stickImg} />
            </If>
            <If condition={get(property, 'state') === propertyState.SOLD_OUT}>
              <SoldOutIcon className="property-card__sold-out" />
            </If>
            <If condition={get(property, 'heroImage.source')}>
              <img
                className="property-card__image"
                src={`${imageHost}/250x250/${get(property, 'heroImage.source')}`}
                alt={get(property, 'slug')}
              />
            </If>

            <If
              condition={
                get(property, 'rankStarCn') ||
                get(property, 'rankStarRow') ||
                get(property, 'rankBlacksheepCn') ||
                get(property, 'rankBlacksheepRow')
              }
            >
              <RankIcon
                t={t}
                rankStarCn={get(property, 'rankStarCn')}
                rankStarRow={get(property, 'rankStarRow')}
                rankBlacksheepCn={get(property, 'rankBlacksheepCn')}
                rankBlacksheepRow={get(property, 'rankBlacksheepRow')}
              />
            </If>

            {property?.status && (
              <Status status={property.status} unpublishedReason={property.unpublishReason} />
            )}
            <If condition={selectable}>
              <div
                onClick={e => {
                  e.nativeEvent.stopPropagation();
                  e.stopPropagation();
                  e.preventDefault();
                  onSelect({ key: get(property, 'id'), value: !selected });
                }}
                className={cardSelectShadow}
              >
                {t('cms.property_card.tag.label.click_to_select')}
              </div>
              <Checkbox
                onClick={e => {
                  e.stopPropagation();
                }}
                checked={selected}
                onChange={e => onSelect({ key: get(property, 'id'), value: e.target.checked })}
                className={cardSelect}
              />
            </If>
          </div>
          <div className={contentBox}>
            <div>
              <div className={name}>{property?.name}</div>

              <div>
                <span className={address}>{`${property?.cityName}, ${property?.countryName}`}</span>
              </div>
            </div>
            <div>
              <div className={bookingType}>
                {t('cms:cms.contract.detail.info_label.landlord_name')}
              </div>
              <div className={description} data-test-id="property_landlord_name">
                {property?.landlord?.name}
              </div>
            </div>
            <div>
              <div className={bookingType}>
                {t('cms:cms.properties.homepage.property_management.sync_type')}
              </div>
              <div className={description}>
                <SyncType t={t} externalSystem={property?.externalSystem} />
              </div>
            </div>
            <div>
              <div className={bookingType}>{t('cms:properties_booking_type_title')}</div>
              <div className={description} data-test-id="property_type_tag">
                {t(`cms:properties_booking_type_${property?.bookingJourney?.toLowerCase()}`)}
              </div>
            </div>
            {!onlyViewPropertyRole() && (
              <div>
                <div className={bookingType}>{t('cms:properties_pending_alert')}</div>
                <If condition={property}>
                  <PendingProgress
                    contractUrl={contractUrl}
                    commissionUrl={commissionUrl}
                    property={property}
                  />
                </If>
              </div>
            )}
          </div>
        </Skeleton>
      </div>
    </Link>
  );
};

Status.propTypes = {
  status: PropTypes.string.isRequired,
};
NewPropertyCard.propTypes = {
  loading: PropTypes.bool.isRequired,
  property: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectable: PropTypes.bool.isRequired,
};
export default NewPropertyCard;
